import { fetchAiCredits } from '~graphql/controllers';
import { handleBuyAiCredits } from '~src/sidebar';
import { getUser } from '~src/user';

/**
 * Checks if user has enough AI credits before performing an AI operation
 * @param {Object} options - Configuration options
 * @param {string} options.featureName - Name of the AI feature for tracking
 * @param {number} options.requiredCredits - Number of credits required (default: 1)
 * @returns {Promise<boolean>} - Returns true if user has enough credits, false otherwise
 */
export async function checkAiCreditsBeforeUse({
  featureName,
  requiredCredits = 1,
} = {}) {
  try {
    // Check if this is an image enhancement feature and org is created before April 1, 2025
    if (
      featureName === 'Image Enhancement' ||
      featureName === 'Face Enhancement' ||
      featureName === 'Make Look Younger'
    ) {
      const userData = await getUser();
      if (
        userData &&
        userData.user &&
        userData.user.org_pointer &&
        userData.user.org_pointer.createdAt
      ) {
        const orgCreatedAt = new Date(userData.user.org_pointer.createdAt);
        const exceptionCutoffDate = new Date('2025-04-01');

        if (orgCreatedAt < exceptionCutoffDate) {
          // Organization created before April 1, 2025 - bypass credit check for image enhancement
          return true;
        }
      }
    }

    // Fetch fresh credits every time
    const credits = await fetchAiCredits();

    if (!credits || !credits.edges || !credits.edges[0]) {
      // No credits data available
      return false;
    }

    const totalCredits =
      credits.edges[0].freeCreditsAmount + credits.edges[0].paidCreditsAmount;

    if (totalCredits < requiredCredits) {
      // Not enough credits - show buy credits modal
      await handleBuyAiCredits();
      return false;
    }

    // User has enough credits
    return true;
  } catch (error) {
    console.error('Error checking AI credits:', error);

    // In case of error, let the feature proceed and handle the error server-side
    return false;
  }
}
